/**
 * Landlord portal API: GET /api/landlord/me, /properties, /units, /tenancies,
 * /invoices, /invoices/{portal_id}/pdf/url.
 * Uses same auth as tenant/admin (Bearer token from authStore); requires role=landlord.
 */
import { API_BASE_URL, getApiHeaders } from "../config";

const opts = () => ({ headers: getApiHeaders(), credentials: "include" });

export function fetchLandlordMe() {
  return fetch(`${API_BASE_URL}/api/landlord/me`, opts()).then((res) => {
    if (res.status === 403) throw new Error("Kein Vermieter-Zugang.");
    if (!res.ok) throw new Error("Profil konnte nicht geladen werden.");
    return res.json();
  });
}

export function fetchLandlordProperties() {
  return fetch(`${API_BASE_URL}/api/landlord/properties`, opts()).then((res) => {
    if (res.status === 403) throw new Error("Kein Vermieter-Zugang.");
    if (!res.ok) throw new Error("Objekte konnten nicht geladen werden.");
    return res.json();
  });
}

export function fetchLandlordUnits(propertyId = null) {
  const url = propertyId
    ? `${API_BASE_URL}/api/landlord/units?property_id=${encodeURIComponent(propertyId)}`
    : `${API_BASE_URL}/api/landlord/units`;
  return fetch(url, opts()).then((res) => {
    if (res.status === 403) throw new Error("Kein Vermieter-Zugang.");
    if (!res.ok) throw new Error("Einheiten konnten nicht geladen werden.");
    return res.json();
  });
}

export function createLandlordUnit(body) {
  return fetch(`${API_BASE_URL}/api/landlord/units`, {
    method: "POST",
    ...opts(),
    body: JSON.stringify(body),
  }).then(async (res) => {
    if (res.status === 403) throw new Error("Kein Vermieter-Zugang oder Objekt nicht zugeordnet.");
    if (!res.ok) {
      let msg = "Einheit konnte nicht erstellt werden.";
      try {
        const data = await res.json();
        if (data.detail) msg = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
      } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  });
}

export function fetchLandlordTenancies() {
  return fetch(`${API_BASE_URL}/api/landlord/tenancies`, opts()).then((res) => {
    if (res.status === 403) throw new Error("Kein Vermieter-Zugang.");
    if (!res.ok) throw new Error("Mietverhältnisse konnten nicht geladen werden.");
    return res.json();
  });
}

export function fetchLandlordInvoices() {
  return fetch(`${API_BASE_URL}/api/landlord/invoices`, opts()).then((res) => {
    if (res.status === 403) throw new Error("Kein Vermieter-Zugang.");
    if (!res.ok) throw new Error("Rechnungen konnten nicht geladen werden.");
    return res.json();
  });
}

/**
 * Short-lived download link for ONE invoice's document.
 *
 * Its own landlord endpoint, not the tenant one. The two share the document
 * machinery on the server and share nothing about who may open it — pointing this
 * client at `/api/tenant/...` would make a tenant's authorization rule the
 * landlord's, and would work for exactly as long as nobody looked.
 *
 * Addressed by `portal_id` — the opaque id the list already returns — and never
 * by a native integer or a provider id. There is deliberately no second,
 * source-specific function beside this one: the backend resolves who may see
 * which invoice, and a caller that had to pick an endpoint would be deciding a
 * question it cannot safely answer.
 *
 * The id is percent-encoded because it carries a `:` separator.
 *
 * Every failure raises the same generic message. A 404 here means "no such
 * invoice OR not yours" (the backend refuses to distinguish those on purpose)
 * and a 503 means the document specifically is unavailable; neither is
 * actionable for a landlord, and spelling out the difference would hand back the
 * information the 404 exists to withhold.
 */
export function fetchLandlordInvoicePdfUrl(portalId) {
  const path = `/api/landlord/invoices/${encodeURIComponent(portalId)}/pdf/url`;
  return fetch(`${API_BASE_URL}${path}`, opts()).then((res) => {
    if (!res.ok) throw new Error("Der Rechnungsbeleg ist momentan nicht verfügbar.");
    return res.json();
  });
}
